import { defineStore } from 'pinia';
import EntitiesApi from '@/api/entitiesAPI';

export const useEntitiesStore = defineStore('entities', {
  state: () => ({
    expendedurias: [] as any[],
    searchTerm: '',
    loading: false,
    error: null as string | null,
    
    // Estado de paginación
    currentPage: 1,
    totalItems: 0,
    pageSize: 50, // Debe coincidir con el PAGE_SIZE de tu backend
  }),

  getters: {
    // Calcula el total de páginas necesarias
    totalPages: (state): number => {
      return Math.ceil(state.totalItems / state.pageSize) || 1;
    },

    // Filtrado local secundario sobre los resultados de la página actual
    filteredExpendedurias: (state) => {
      if (!state.searchTerm.trim()) return state.expendedurias;
      const query = state.searchTerm.toLowerCase();
      return state.expendedurias.filter(
        (item) =>
          item.name?.toLowerCase().includes(query) ||
          item.email?.toLowerCase().includes(query)
      );
    },
  },

  actions: {
    /**
     * Carga las expendedurías desde la API.
     * @param page Número de página a solicitar (por defecto usa la página actual o 1)
     */
    async fetchExpendedurias(params?: number | Record<string, any>) {
      this.loading = true;
      this.error = null;

      let pageNumber = this.currentPage;
      let queryParams: Record<string, any> = {};

      if (typeof params === 'number') {
        pageNumber = params;
        queryParams = { page: pageNumber };
      } else if (typeof params === 'object' && params !== null) {
        pageNumber = Number(params.page) || 1;
        queryParams = { ...params, page: pageNumber };
      } else {
        queryParams = { page: pageNumber };
      }

      this.currentPage = pageNumber;

      try {
        // Pasa el término de búsqueda y el número de página a la API
        const data = await EntitiesApi.searchExpendeduria(queryParams);

        if (data && data.results) {
          // Respuesta paginada estándar de DRF
          this.expendedurias = data.results;
          this.totalItems = data.count;
        } else {
          // Fallback en caso de que la API devuelva un array llano
          this.expendedurias = Array.isArray(data) ? data : [];
          this.totalItems = this.expendedurias.length;
        }
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Error al obtener expendedurías';
        console.error('Error fetching expendedurias:', error);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Actualiza el término de búsqueda y reinicia a la página 1
     */
    setSearchTerm(term: string) {
      this.searchTerm = term;
      this.fetchExpendedurias(1);
    },

    /**
     * Cambia de página directamente
     */
    setPage(page: number) {
      if (page >= 1 && page <= this.totalPages) {
        this.fetchExpendedurias(page);
      }
    },

    async updateExpendeduria(updateData: any) {
      this.loading = true;
      try {
        const payload = {
          name: updateData.name,
          notes: updateData.notes,
          lesson_status: updateData.lesson_status,
          date_first_arrive: updateData.date_first_arrive,
          lesson_date: updateData.lesson_date,
          follow_up_7_days: updateData.follow_up_7_days,
          follow_up_20_days: updateData.follow_up_20_days
        }

        const response = await EntitiesApi.updateExpendeduria(updateData.id, payload);

        const index = this.expendedurias.findIndex((item) => item.id === updateData.id)
        if (index !== -1) {
          this.expendedurias[index] = { ...this.expendedurias[index], ...response }
        }
      } catch (error) {
        this.error = error instanceof Error ? error.message : 'Error al actualizar expendeduría';
        console.error('Error updating expendeduria:', error);
        throw error; // Re-lanzar para que el componente pueda manejarlo
      } finally {
        this.loading = false;
      }
    },
  },
});